<template>
  <div 
    class="flex items-center space-x-2 cursor-pointer hover:bg-muted/30 rounded p-1 transition-colors"
    @click="$emit('activate')"
  >
    <!-- Scene Color Square -->
    <div 
      class="w-5 h-5 rounded border-2 border-gray-200 flex-shrink-0 relative overflow-hidden"
      :style="sceneStyle"
    >
      <!-- Multi-color gradient for scenes with multiple colors -->
      <div 
        v-if="sceneColors.length > 1"
        class="absolute inset-0"
        :style="gradientStyle"
      ></div>
      
      <!-- Single color background -->
      <div 
        v-else-if="sceneColors.length === 1"
        class="absolute inset-0"
        :style="{ backgroundColor: sceneColors[0] }"
      ></div>
      
      <!-- Default scene color -->
      <div 
        v-else
        class="absolute inset-0 bg-gradient-to-br from-blue-400 to-purple-500"
      ></div>
      
      <!-- Active indicator -->
      <div 
        v-if="isActive"
        class="absolute inset-0 border-2 border-white shadow-lg"
      ></div>
    </div>
    
    <!-- Scene Name -->
    <span class="text-sm truncate flex-1" :class="{ 'font-medium': isActive }">
      {{ scene.name }}
    </span>
    
    <!-- Scene Type Badge -->
    <Badge 
      v-if="sceneType"
      variant="outline" 
      class="text-xs flex-shrink-0"
    >
      {{ sceneType }}
    </Badge>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Badge } from '@/components/ui/badge'

interface SceneLight {
  light_id: string
  color?: string
  brightness?: number
  on: boolean
}

interface Props {
  scene: {
    id: string
    name: string
    type?: 'LightScene' | 'GroupScene'
    lights?: SceneLight[]
    metadata?: Record<string, unknown>
  }
  isActive?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isActive: false
})

defineEmits<{
  activate: []
}>()

const sceneColors = computed(() => {
  if (!props.scene.lights) return []
  
  return props.scene.lights
    .filter(light => light.on && light.color)
    .map(light => light.color!)
    .slice(0, 4) // Max 4 colors for gradient
})

const sceneStyle = computed(() => {
  const baseStyle: Record<string, string> = {}
  
  if (props.isActive) {
    baseStyle.borderColor = '#3b82f6' // blue-500
    baseStyle.borderWidth = '2px'
  }
  
  return baseStyle
})

const gradientStyle = computed(() => {
  if (sceneColors.value.length <= 1) return {}
  
  const colors = sceneColors.value
  
  if (colors.length === 2) {
    return {
      background: `linear-gradient(135deg, ${colors[0]} 0%, ${colors[1]} 100%)`
    }
  }
  
  if (colors.length === 3) {
    return {
      background: `linear-gradient(135deg, ${colors[0]} 0%, ${colors[1]} 50%, ${colors[2]} 100%)`
    }
  }
  
  // 4 colors - create a quadrant effect
  return {
    background: `conic-gradient(from 0deg, ${colors[0]} 0deg 90deg, ${colors[1]} 90deg 180deg, ${colors[2]} 180deg 270deg, ${colors[3]} 270deg 360deg)`
  }
})

const sceneType = computed(() => {
  if (props.scene.type === 'LightScene') return 'Light'
  if (props.scene.type === 'GroupScene') return 'Room'
  return undefined
})
</script>